// src/components/workspace/costing/ODCCategoriesPage.tsx
// Dedicated page for managing Other Direct Costs (ODCs) by contract type

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, ArrowLeft, Plus, Trash2, Save, Loader2, Sparkles,
  CheckCircle, DollarSign, Edit2, Truck, Utensils, Wrench,
  Monitor, Plane, Users, Shield, Building2, BookOpen, Boxes,
  Fuel, Car, HardHat, AlertCircle
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../lib/pricing/utils';
import toast from 'react-hot-toast';

interface ODCCategory {
  id: string;
  name: string;
  description: string;
  category_type: string;
  estimated_unit_cost: number | null;
  unit: string;
  estimated_annual_quantity: number;
  markup_percentage: number;
  is_recurring: boolean;
  applicable_contract_types: string[];
  notes: string;
  is_active: boolean;
  isNew?: boolean;
  isEditing?: boolean;
}

interface ContractTypeTemplate {
  contract_type: string;
  display_name: string;
  typical_odc_categories: any[];
}

const CATEGORY_TYPES = [
  { value: 'travel', label: 'Travel', icon: Plane, color: 'blue' },
  { value: 'equipment', label: 'Equipment', icon: Monitor, color: 'purple' },
  { value: 'materials', label: 'Materials', icon: Boxes, color: 'orange' },
  { value: 'supplies', label: 'Supplies', icon: Package, color: 'teal' },
  { value: 'software', label: 'Software', icon: Monitor, color: 'indigo' },
  { value: 'subcontractor', label: 'Subcontractor', icon: Users, color: 'pink' },
  { value: 'facilities', label: 'Facilities', icon: Building2, color: 'gray' },
  { value: 'training', label: 'Training', icon: BookOpen, color: 'green' },
  { value: 'vehicles', label: 'Vehicles', icon: Car, color: 'red' },
  { value: 'fuel', label: 'Fuel', icon: Fuel, color: 'amber' },
  { value: 'food_services', label: 'Food Services', icon: Utensils, color: 'yellow' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'slate' },
  { value: 'communications', label: 'Communications', icon: Monitor, color: 'cyan' },
  { value: 'other', label: 'Other', icon: Package, color: 'gray' }
];

const CONTRACT_TYPE_ICONS: Record<string, React.ComponentType<any>> = {
  'professional_services': Users,
  'it_services': Monitor,
  'maintenance': Wrench,
  'transportation': Truck,
  'food_services': Utensils,
  'security_services': Shield,
  'healthcare': Users,
  'training': BookOpen,
  'logistics': Boxes,
  'construction': HardHat
};

// Suggested ODCs by contract type
const SUGGESTED_ODCS: Record<string, any[]> = {
  'all': [
    { name: 'Travel', type: 'travel', unit: 'trip', cost: 1500, description: 'Employee travel expenses' },
    { name: 'Training', type: 'training', unit: 'course', cost: 2000, description: 'Employee training and certifications' },
    { name: 'Equipment', type: 'equipment', unit: 'each', cost: 5000, description: 'Office and field equipment' }
  ],
  'it_services': [
    { name: 'Cloud Services (AWS/Azure)', type: 'software', unit: 'month', cost: 5000, description: 'Cloud infrastructure costs' },
    { name: 'Software Licenses', type: 'software', unit: 'year', cost: 10000, description: 'Commercial software licenses' },
    { name: 'Development Tools', type: 'software', unit: 'year', cost: 3000, description: 'IDEs, testing tools, CI/CD' },
    { name: 'Security Tools', type: 'software', unit: 'year', cost: 8000, description: 'SIEM, vulnerability scanners' },
    { name: 'Hardware', type: 'equipment', unit: 'each', cost: 2500, description: 'Laptops, monitors, peripherals' }
  ],
  'maintenance': [
    { name: 'Repair Parts', type: 'materials', unit: 'month', cost: 3000, description: 'Replacement parts inventory' },
    { name: 'Tools & Equipment', type: 'equipment', unit: 'year', cost: 8000, description: 'Hand tools, power tools, diagnostic equipment' },
    { name: 'Cleaning Supplies', type: 'supplies', unit: 'month', cost: 1500, description: 'Janitorial and cleaning chemicals' },
    { name: 'Safety Equipment', type: 'equipment', unit: 'year', cost: 3000, description: 'PPE, first aid, safety gear' },
    { name: 'Vehicle Expenses', type: 'vehicles', unit: 'month', cost: 2000, description: 'Service vehicle maintenance and fuel' },
    { name: 'HVAC Filters/Parts', type: 'materials', unit: 'quarter', cost: 2500, description: 'HVAC system maintenance supplies' }
  ],
  'transportation': [
    { name: 'Fuel', type: 'fuel', unit: 'month', cost: 8000, description: 'Diesel/gasoline for fleet vehicles' },
    { name: 'Vehicle Lease', type: 'vehicles', unit: 'month', cost: 5000, description: 'Monthly vehicle lease payments' },
    { name: 'Vehicle Maintenance', type: 'maintenance', unit: 'month', cost: 2500, description: 'Routine maintenance and repairs' },
    { name: 'Insurance', type: 'other', unit: 'year', cost: 15000, description: 'Commercial auto insurance' },
    { name: 'Tolls & Parking', type: 'other', unit: 'month', cost: 800, description: 'Highway tolls and parking fees' },
    { name: 'GPS/Telematics', type: 'equipment', unit: 'month', cost: 500, description: 'Fleet tracking systems' },
    { name: 'Tires', type: 'materials', unit: 'year', cost: 6000, description: 'Replacement tires for fleet' }
  ],
  'food_services': [
    { name: 'Food & Provisions', type: 'food_services', unit: 'month', cost: 15000, description: 'Raw ingredients and food supplies' },
    { name: 'Kitchen Equipment', type: 'equipment', unit: 'year', cost: 8000, description: 'Cooking equipment, refrigeration' },
    { name: 'Smallwares', type: 'supplies', unit: 'quarter', cost: 2000, description: 'Utensils, pans, serving equipment' },
    { name: 'Cleaning Chemicals', type: 'supplies', unit: 'month', cost: 1200, description: 'Sanitizers, detergents' },
    { name: 'Disposables', type: 'supplies', unit: 'month', cost: 3000, description: 'Plates, napkins, takeout containers' },
    { name: 'Uniforms', type: 'supplies', unit: 'year', cost: 2500, description: 'Chef coats, aprons, hairnets' },
    { name: 'Health Permits', type: 'other', unit: 'year', cost: 500, description: 'Food service permits and certifications' }
  ],
  'security_services': [
    { name: 'Uniforms', type: 'supplies', unit: 'year', cost: 5000, description: 'Security officer uniforms' },
    { name: 'Radio Equipment', type: 'communications', unit: 'year', cost: 3000, description: 'Two-way radios and accessories' },
    { name: 'Vehicles', type: 'vehicles', unit: 'month', cost: 2000, description: 'Patrol vehicle lease and maintenance' },
    { name: 'Weapons & Ammo', type: 'equipment', unit: 'year', cost: 4000, description: 'Firearms and ammunition (if armed)' },
    { name: 'Training', type: 'training', unit: 'year', cost: 6000, description: 'Security certifications and training' },
    { name: 'Body Armor', type: 'equipment', unit: 'year', cost: 3000, description: 'Protective vests (if required)' }
  ]
};

export function ODCCategoriesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<ODCCategory[]>([]);
  const [templates, setTemplates] = useState<ContractTypeTemplate[]>([]);
  const [selectedContractType, setSelectedContractType] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    if (profile) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load ODC categories
      const { data: odcData, error: odcError } = await supabase
        .from('company_odc_categories')
        .select('*')
        .eq('profile_id', profile?.id)
        .order('category_type, name');

      if (odcError && odcError.code !== 'PGRST116') {
        console.error('Error loading ODC categories:', odcError);
      }

      // Load contract type templates
      const { data: templateData, error: templateError } = await supabase
        .from('contract_type_templates')
        .select('contract_type, display_name, typical_odc_categories');

      if (templateError && templateError.code !== 'PGRST116') {
        console.error('Error loading templates:', templateError);
      }

      setCategories(odcData || []);
      setTemplates(templateData || []);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load ODC categories');
    } finally {
      setLoading(false);
    }
  };

  const addCategory = () => {
    const newCategory: ODCCategory = {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      category_type: 'other',
      estimated_unit_cost: null,
      unit: 'each',
      estimated_annual_quantity: 1,
      markup_percentage: 0,
      is_recurring: true,
      applicable_contract_types: ['all'],
      notes: '',
      is_active: true,
      isNew: true,
      isEditing: true
    };
    setCategories([...categories, newCategory]);
    setEditingId(newCategory.id);
  };

  const addFromSuggestion = (suggestion: any) => {
    const exists = categories.some(
      cat => cat.name.toLowerCase() === suggestion.name.toLowerCase()
    );
    if (exists) {
      toast.error('This ODC category already exists');
      return;
    }

    const newCategory: ODCCategory = {
      id: `new-${Date.now()}`,
      name: suggestion.name,
      description: suggestion.description || '',
      category_type: suggestion.type,
      estimated_unit_cost: suggestion.cost,
      unit: suggestion.unit,
      estimated_annual_quantity: 12, // Default to 12 for monthly items
      markup_percentage: 0,
      is_recurring: true,
      applicable_contract_types: selectedContractType === 'all' ? ['all'] : [selectedContractType],
      notes: '',
      is_active: true,
      isNew: true,
      isEditing: false
    };
    setCategories([...categories, newCategory]);
    toast.success(`Added ${suggestion.name}`);
  };

  const updateCategory = (id: string, updates: Partial<ODCCategory>) => {
    setCategories(categories.map(cat =>
      cat.id === id ? { ...cat, ...updates } : cat
    ));
  };

  const deleteCategory = (id: string) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate categories
      const invalidCategories = categories.filter(cat => !cat.name.trim());
      if (invalidCategories.length > 0) {
        toast.error('Please provide a name for all ODC categories');
        return;
      }

      // Separate new and existing categories
      const newCategories = categories.filter(cat => cat.isNew);
      const existingCategories = categories.filter(cat => !cat.isNew);

      // Delete removed categories
      const { data: currentData } = await supabase
        .from('company_odc_categories')
        .select('id')
        .eq('profile_id', profile?.id);

      const currentIds = currentData?.map(c => c.id) || [];
      const keepIds = existingCategories.map(c => c.id);
      const deleteIds = currentIds.filter(id => !keepIds.includes(id));

      if (deleteIds.length > 0) {
        await supabase
          .from('company_odc_categories')
          .delete()
          .in('id', deleteIds);
      }

      // Insert new categories
      if (newCategories.length > 0) {
        const toInsert = newCategories.map(({ id, isNew, isEditing, ...cat }) => ({
          ...cat,
          profile_id: profile?.id
        }));

        const { error: insertError } = await supabase
          .from('company_odc_categories')
          .insert(toInsert);

        if (insertError) throw insertError;
      }

      // Update existing categories
      for (const cat of existingCategories) {
        const { id, isNew, isEditing, ...updates } = cat;
        const { error: updateError } = await supabase
          .from('company_odc_categories')
          .update(updates)
          .eq('id', id);

        if (updateError) throw updateError;
      }

      toast.success('ODC categories saved successfully!');
      await loadData();

    } catch (error) {
      console.error('Error saving ODC categories:', error);
      toast.error('Failed to save ODC categories');
    } finally {
      setSaving(false);
    }
  };

  const calculateAnnualCost = (category: ODCCategory): number => {
    const baseCost = (category.estimated_unit_cost || 0) * category.estimated_annual_quantity;
    const markup = baseCost * (category.markup_percentage / 100);
    return baseCost + markup;
  };

  const totalAnnualODCs = categories.reduce((sum, cat) => sum + calculateAnnualCost(cat), 0);

  const getCategoryIcon = (type: string) => {
    const categoryType = CATEGORY_TYPES.find(ct => ct.value === type);
    return categoryType?.icon || Package;
  };

  const filteredCategories = filterType === 'all'
    ? categories
    : categories.filter(cat => cat.category_type === filterType);

  const suggestions = SUGGESTED_ODCS[selectedContractType] || SUGGESTED_ODCS['all'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-bw-primary" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'max-w-6xl mx-auto p-6'}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {!embedded && (
          <button
            onClick={() => navigate('/workspace/costing')}
            className="flex items-center text-sm text-bw-muted hover:text-bw-ink mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Costing Studio
          </button>
          )}
          <h1 className="text-2xl font-bold text-bw-ink flex items-center">
            <Package className="h-6 w-6 mr-2 text-bw-primary" />
            Other Direct Costs (ODCs)
          </h1>
          <p className="text-bw-muted mt-1">
            Configure travel, equipment, materials, supplies, and other direct costs for your proposals
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Categories
        </button>
      </div>

      {/* Total ODC Summary */}
      <div className="bg-bw-surface-subtle rounded-lg p-4 border border-purple-200 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-bw-ink">Total Estimated Annual ODCs</h3>
            <p className="text-sm text-bw-muted">{categories.length} categories configured</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-bw-primary">{formatCurrency(totalAnnualODCs)}</div>
            <div className="text-sm text-bw-primary">/year</div>
          </div>
        </div>
      </div>

      {/* Contract Type Selector & Suggestions */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Sparkles className="h-5 w-5 text-bw-primary mr-2" />
            <h3 className="font-semibold text-bw-ink">Suggested ODCs by Contract Type</h3>
          </div>
          <select
            value={selectedContractType}
            onChange={(e) => setSelectedContractType(e.target.value)}
            className="px-3 py-1.5 text-sm border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
          >
            <option value="all">General/All Types</option>
            {templates.map(template => (
              <option key={template.contract_type} value={template.contract_type}>
                {template.display_name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((suggestion, index) => {
            const isAdded = categories.some(
              cat => cat.name.toLowerCase() === suggestion.name.toLowerCase()
            );
            const Icon = getCategoryIcon(suggestion.type);
            return (
              <button
                key={index}
                onClick={() => !isAdded && addFromSuggestion(suggestion)}
                disabled={isAdded}
                className={`px-3 py-1.5 rounded-full text-sm flex items-center transition-colors ${
                  isAdded
                    ? 'bg-success-bg text-success cursor-default'
                    : 'bg-bw-surface border border-purple-200 text-bw-primary hover:bg-bw-primary-tint'
                }`}
              >
                {isAdded ? (
                  <CheckCircle className="h-3 w-3 mr-1" />
                ) : (
                  <Plus className="h-3 w-3 mr-1" />
                )}
                <Icon className="h-3 w-3 mr-1" />
                {suggestion.name}
                <span className="ml-1 text-xs text-bw-muted">${suggestion.cost}/{suggestion.unit}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter by Category Type */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-bw-muted">Filter by type:</span>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 text-sm border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
          >
            <option value="all">All Types</option>
            {CATEGORY_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={addCategory}
          className="flex items-center px-3 py-1.5 text-sm text-bw-primary hover:bg-bw-primary-tint rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Custom ODC
        </button>
      </div>

      {/* ODC Categories List */}
      <div className="bg-bw-surface rounded-xl border border-bw-border overflow-hidden">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-bw-muted">No ODC categories configured</p>
            <p className="text-sm text-bw-muted mt-1">
              Click the suggestions above or add a custom category
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredCategories.map((category) => {
              const Icon = getCategoryIcon(category.category_type);
              const annualCost = calculateAnnualCost(category);

              return (
                <div
                  key={category.id}
                  className={`p-4 ${category.isEditing ? 'bg-bw-primary-tint' : 'hover:bg-bw-surface-subtle'} transition-colors`}
                >
                  {category.isEditing ? (
                    // Edit mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-bw-ink mb-1">Name *</label>
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => updateCategory(category.id, { name: e.target.value })}
                            placeholder="e.g., Travel Expenses"
                            className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-bw-ink mb-1">Category Type</label>
                          <select
                            value={category.category_type}
                            onChange={(e) => updateCategory(category.id, { category_type: e.target.value })}
                            className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          >
                            {CATEGORY_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-bw-ink mb-1">Unit</label>
                          <input
                            type="text"
                            value={category.unit}
                            onChange={(e) => updateCategory(category.id, { unit: e.target.value })}
                            placeholder="e.g., trip, month, each"
                            className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-bw-ink mb-1">Unit Cost ($)</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bw-muted" />
                            <input
                              type="number"
                              value={category.estimated_unit_cost || ''}
                              onChange={(e) => updateCategory(category.id, { estimated_unit_cost: parseFloat(e.target.value) || null })}
                              placeholder="0.00"
                              className="w-full pl-8 pr-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-bw-ink mb-1">Annual Quantity</label>
                          <input
                            type="number"
                            min="1"
                            value={category.estimated_annual_quantity}
                            onChange={(e) => updateCategory(category.id, { estimated_annual_quantity: parseInt(e.target.value) || 1 })}
                            className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-bw-ink mb-1">Markup (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={category.markup_percentage}
                            onChange={(e) => updateCategory(category.id, { markup_percentage: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={category.is_recurring}
                              onChange={(e) => updateCategory(category.id, { is_recurring: e.target.checked })}
                              className="rounded border-bw-border text-bw-primary focus:ring-bw-primary"
                            />
                            <span className="ml-2 text-sm text-bw-ink">Recurring</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Description</label>
                        <textarea
                          value={category.description}
                          onChange={(e) => updateCategory(category.id, { description: e.target.value })}
                          placeholder="Brief description of this ODC category..."
                          rows={2}
                          className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                        />
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="text-sm">
                          <span className="text-bw-muted">Estimated Annual: </span>
                          <span className="font-semibold text-bw-primary">{formatCurrency(annualCost)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => deleteCategory(category.id)}
                            className="px-3 py-1.5 text-sm text-urgent hover:bg-urgent-bg rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              updateCategory(category.id, { isEditing: false });
                              setEditingId(null);
                            }}
                            className="px-4 py-1.5 text-sm bg-bw-primary text-white rounded-lg hover:bg-bw-primary transition-colors"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-lg bg-${CATEGORY_TYPES.find(t => t.value === category.category_type)?.color || 'gray'}-100`}>
                          <Icon className={`h-5 w-5 text-${CATEGORY_TYPES.find(t => t.value === category.category_type)?.color || 'gray'}-600`} />
                        </div>
                        <div>
                          <div className="flex items-center">
                            <h4 className="font-medium text-bw-ink">{category.name}</h4>
                            <span className="ml-2 px-1.5 py-0.5 bg-bw-surface-subtle text-bw-muted text-xs rounded">
                              {CATEGORY_TYPES.find(t => t.value === category.category_type)?.label || 'Other'}
                            </span>
                            {category.is_recurring && (
                              <span className="ml-1 px-1.5 py-0.5 bg-bw-primary-tint text-bw-primary text-xs rounded">
                                Recurring
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-bw-muted mt-0.5">{category.description || 'No description'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <div className="text-sm text-bw-muted">
                            ${(category.estimated_unit_cost || 0).toFixed(2)} x {category.estimated_annual_quantity} {category.unit}
                            {category.markup_percentage > 0 && ` (+${category.markup_percentage}%)`}
                          </div>
                          <div className="text-lg font-semibold text-bw-primary">
                            {formatCurrency(annualCost)}/yr
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              updateCategory(category.id, { isEditing: true });
                              setEditingId(category.id);
                            }}
                            className="p-2 text-bw-muted hover:text-bw-primary transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteCategory(category.id)}
                            className="p-2 text-bw-muted hover:text-urgent transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary by Category Type */}
      {categories.length > 0 && (
        <div className="mt-6 bg-bw-surface-subtle rounded-lg p-4">
          <h4 className="font-medium text-bw-ink mb-3">Summary by Category</h4>
          <div className="grid grid-cols-4 gap-4">
            {CATEGORY_TYPES.filter(type =>
              categories.some(cat => cat.category_type === type.value)
            ).map(type => {
              const typeCategories = categories.filter(cat => cat.category_type === type.value);
              const typeTotal = typeCategories.reduce((sum, cat) => sum + calculateAnnualCost(cat), 0);
              const Icon = type.icon;

              return (
                <div key={type.value} className="flex items-center justify-between p-2 bg-bw-surface rounded-lg">
                  <div className="flex items-center">
                    <Icon className={`h-4 w-4 text-${type.color}-600 mr-2`} />
                    <span className="text-sm text-bw-ink">{type.label}</span>
                    <span className="ml-1 text-xs text-bw-muted">({typeCategories.length})</span>
                  </div>
                  <span className="text-sm font-medium">{formatCurrency(typeTotal)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-bw-primary-tint rounded-lg border border-blue-200 p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-bw-primary mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-bw-primary">Tips for ODC Configuration</h4>
            <ul className="text-sm text-bw-primary mt-2 space-y-1">
              <li>- ODCs are typically passed through at cost or with a small handling fee (0-10%)</li>
              <li>- Travel costs should follow the Federal Travel Regulation (FTR) or JTR rates</li>
              <li>- Material handling fees are limited to 5% under some contract types</li>
              <li>- Subcontractor costs may be subject to G&A but typically not overhead</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
